import { Box, Paper, Stack, Typography } from "@mui/material";
import { formatKw, formatMwh, formatPct, formatRunTimestamp } from "./pvGenerationUtils";

function KpiCard({ label, value, tone }) {
  return (
    <Paper variant="outlined" sx={{ py: 0.75, px: 1.25, borderRadius: 2, flex: "1 1 130px", minWidth: 130 }}>
      <Stack spacing={0.125}>
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 800,
            lineHeight: 1.2,
            color: tone === "negative" ? "error.main" : tone === "positive" ? "success.main" : "text.primary",
          }}
        >
          {value}
        </Typography>
      </Stack>
    </Paper>
  );
}

function toneFor(value) {
  if (value == null || !Number.isFinite(value)) return undefined;
  if (value < 0) return "negative";
  if (value > 0) return "positive";
  return undefined;
}

function KpiRow({ historicalMwh, row, measuredAvailable }) {
  const { forecastMwh, differenceMwh, errorPct, peakDifferenceKw, maxDifferenceKw, accuracy } = row;
  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
      <KpiCard label="Historical generation" value={formatMwh(historicalMwh)} />
      <KpiCard label="Forecast generation" value={formatMwh(forecastMwh)} />
      <KpiCard label="Difference" value={formatMwh(differenceMwh)} tone={toneFor(differenceMwh)} />
      <KpiCard label="Forecast error" value={formatPct(errorPct)} tone={toneFor(errorPct)} />
      <KpiCard label="Peak difference" value={formatKw(peakDifferenceKw)} tone={toneFor(peakDifferenceKw)} />
      <KpiCard label="Max difference" value={formatKw(maxDifferenceKw)} />
      {measuredAvailable && accuracy && (
        <>
          <KpiCard label="MAE" value={formatKw(accuracy.mae)} />
          <KpiCard label="RMSE" value={formatKw(accuracy.rmse)} />
          <KpiCard label="Bias" value={formatKw(accuracy.bias)} tone={toneFor(accuracy.bias)} />
          <KpiCard
            label="Forecast accuracy"
            value={accuracy.accuracyPct != null ? `${accuracy.accuracyPct.toFixed(1)}%` : "—"}
          />
        </>
      )}
    </Stack>
  );
}

export default function PvKpiCards({ historicalMwh, forecastRows, measuredAvailable, timeZone }) {
  if (historicalMwh == null && forecastRows.length === 0) return null;

  if (forecastRows.length === 0) {
    return (
      <Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
          <KpiCard label="Historical generation" value={formatMwh(historicalMwh)} />
        </Stack>
      </Box>
    );
  }

  return (
    <Stack spacing={1.25}>
      {forecastRows.map((row) => (
        <Box key={row.run.run_id}>
          {forecastRows.length > 1 && (
            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", display: "block", mb: 0.5 }}>
              vs forecast run · {formatRunTimestamp(row.run.created_at, timeZone)}
            </Typography>
          )}
          <KpiRow historicalMwh={historicalMwh} row={row} measuredAvailable={measuredAvailable} />
        </Box>
      ))}
    </Stack>
  );
}
